import type { Breadcrumb, Event } from '@sentry/react';

const SENSITIVE_PARAMS = new Set(['token', 'setup_intent', 'setup_intent_client_secret']);

/** Strip URL-borne secrets before sending them to Sentry. */
export function stripSensitiveUrl(url: unknown): string | undefined {
  if (typeof url !== 'string') return undefined;

  try {
    const parsed = new URL(url, location.origin);
    let changed = false;

    for (const param of Array.from(parsed.searchParams.keys())) {
      if (SENSITIVE_PARAMS.has(param.toLowerCase())) {
        parsed.searchParams.delete(param);
        changed = true;
      }
    }

    if (
      (parsed.pathname.includes('/auth/oidc-callback') || parsed.pathname.includes('/google-idp/callback')) &&
      parsed.search
    ) {
      parsed.search = '';
      changed = true;
    }

    if (!changed) return url;
    if (url.startsWith('/') && !url.startsWith('//')) {
      return parsed.pathname + parsed.search + parsed.hash;
    }
    return parsed.toString();
  } catch {
    return url.split('?')[0];
  }
}

// Span attributes the SDK fills with the page URL, e.g. `url.full` on pageload/navigation spans.
const URL_ATTRIBUTES = ['url.full', 'http.url', 'url'];

function scrubUrlAttributes(data: Record<string, unknown> | undefined) {
  if (!data) return;

  for (const key of URL_ATTRIBUTES) {
    const url = stripSensitiveUrl(data[key]);
    if (url !== undefined) data[key] = url;
  }
}

function scrubEvent<T extends Event>(event: T): T {
  const url = stripSensitiveUrl(event.request?.url);
  if (url !== undefined && event.request) event.request.url = url;

  const referrer = stripSensitiveUrl(event.request?.headers?.Referer);
  if (referrer !== undefined && event.request?.headers) {
    event.request.headers.Referer = referrer;
  }

  scrubUrlAttributes(event.contexts?.trace?.data);
  for (const span of event.spans ?? []) scrubUrlAttributes(span.data);

  return event;
}

function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  if (!breadcrumb.data) return breadcrumb;

  for (const field of ['from', 'to', 'url']) {
    const url = stripSensitiveUrl(breadcrumb.data[field]);
    if (url !== undefined) breadcrumb.data[field] = url;
  }

  return breadcrumb;
}

export const sentryScrubbingHooks = {
  beforeSend: scrubEvent,
  beforeSendTransaction: scrubEvent,
  beforeBreadcrumb: scrubBreadcrumb,
};
